import { useEffect, useRef, useState } from "react";
import { DC_COMMON_TEXT, BULK_ADD_COMMENT_LABEL, BULK_ADD_COMMENT_ID } from "@/utils/draftCalendarFields";

interface BulkAddCommentDialogProps {
  open: boolean;
  selectedCount: number;
  submitting?: boolean;
  onCancel: () => void;
  onSave: (comment: string) => void | Promise<void>;
}

/**
 * Dialog for adding the same comment to multiple selected Draft Calendar meetings at once,
 * regardless of their status, role, or state. Comments added here are not tracked in history.
 */
export function BulkAddCommentDialog({ open, selectedCount, submitting = false, onCancel, onSave }: BulkAddCommentDialogProps) {
  const [comment, setComment] = useState("");
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (open) {
      previousFocusRef.current = document.activeElement as HTMLElement | null;
      setComment("");
      setError(null);
      requestAnimationFrame(() => textareaRef.current?.focus());
    } else {
      previousFocusRef.current?.focus();
    }
  }, [open]);

  if (!open) return null;

  function handleSave() {
    const trimmed = comment.trim();
    if (!trimmed) {
      setError("Comment is required.");
      return;
    }
    void onSave(trimmed);
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
      onClick={onCancel}
      onKeyDown={(e) => { if (e.key === "Escape") onCancel(); }}
      tabIndex={-1}
      aria-hidden="true"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={BULK_ADD_COMMENT_ID.DIALOG_TITLE}
        className="w-[450px] max-w-[calc(100vw-2rem)] border border-slate-300 bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-200 bg-black px-4 py-3">
          <h2 id={BULK_ADD_COMMENT_ID.DIALOG_TITLE} className="text-base font-semibold text-white">
            {BULK_ADD_COMMENT_LABEL.DIALOG_TITLE_PREFIX} Comment{selectedCount > 1 ? "s" : ""}
          </h2>
          <button type="button" onClick={onCancel} aria-label={BULK_ADD_COMMENT_LABEL.CLOSE} className="text-white hover:text-slate-300">
            &#x2715;
          </button>
        </div>

        {/* Content */}
        <div className="px-5 py-5">
          <label htmlFor={BULK_ADD_COMMENT_ID.COMMENT_INPUT} className="mb-1 block text-sm font-medium text-slate-700">
            {BULK_ADD_COMMENT_LABEL.COMMENT}
          </label>
          <textarea
            id={BULK_ADD_COMMENT_ID.COMMENT_INPUT}
            ref={textareaRef}
            rows={5}
            value={comment}
            onChange={(e) => { setComment(e.target.value); if (error) setError(null); }}
            placeholder={DC_COMMON_TEXT.COMMENT_PLACEHOLDER}
            className="Add-Notes w-full"
          />
          {error && <p role="alert" className="mt-1 text-xs text-red-600">{error}</p>}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 border-t border-slate-200 px-4 py-3">
          <button type="button" onClick={onCancel} className="Add-Cancel-Button border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">
            {BULK_ADD_COMMENT_LABEL.CANCEL}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={submitting}
            className="border border-slate-900 bg-slate-900 px-4 py-2 text-sm text-white hover:bg-slate-800 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? "Saving\u2026" : BULK_ADD_COMMENT_LABEL.SAVE}
          </button>
        </div>
      </div>
    </div>
  );
}
